import type { ForceBankSettlementResponse } from '../lib/types';

export type StepState = 'pending' | 'running' | 'success' | 'failed' | 'unsupported' | 'warning';

export interface TimelineStep {
  label: string;
  state: StepState;
}

const STEP_LABELS = [
  'Authenticated and authorised',
  'KoreID resolved',
  'Linked bank account validated',
  'Force resettle submitted',
  'Force resync submitted',
  'Settlement reference received',
  'Bank-searchable reference received',
  'PACS.028 nudge submitted',
  'Bank acknowledgement captured',
  'Payment confirmation reconciled',
  'Audit trail recorded',
] as const;

const OK = new Set(['SUCCESS', 'COMPLETED', 'CONFIRMED', 'ACCEPTED', 'ACSC', 'ACCC']);
const PENDING = new Set(['PENDING', 'PROCESSING', 'ACSP', 'UNCONFIRMED']);

function stepFromStatus(status: string | undefined): StepState {
  if (!status) return 'pending';
  const s = status.toUpperCase();
  if (OK.has(s)) return 'success';
  if (PENDING.has(s)) return 'warning';
  if (s === 'UNSUPPORTED') return 'unsupported';
  if (s === 'FAILED' || s === 'REJECTED' || s === 'ERROR' || s === 'NOT_FOUND') return 'failed';
  return 'warning';
}

/** Derives the 11-step timeline from a real response (or all-running while in flight). */
export function deriveSteps(response: ForceBankSettlementResponse | null, inFlight: boolean): TimelineStep[] {
  if (!response) {
    return STEP_LABELS.map((label, i) => ({ label, state: inFlight && i === 0 ? 'running' : 'pending' }));
  }
  const r = response;
  return [
    { label: STEP_LABELS[0], state: 'success' },
    { label: STEP_LABELS[1], state: r.account.resolved ? 'success' : 'failed' },
    { label: STEP_LABELS[2], state: r.account.eligible ? 'success' : 'failed' },
    { label: STEP_LABELS[3], state: stepFromStatus(r.settlement.status) },
    { label: STEP_LABELS[4], state: stepFromStatus(r.resync.status) },
    { label: STEP_LABELS[5], state: r.settlement.settlementReference ? 'success' : 'pending' },
    { label: STEP_LABELS[6], state: (r.settlement.bankSearchableReference ?? r.bankAcknowledgement.bankSearchableReference) ? 'success' : 'pending' },
    { label: STEP_LABELS[7], state: r.pacs028.supported ? stepFromStatus(r.pacs028.status) : 'unsupported' },
    { label: STEP_LABELS[8], state: stepFromStatus(r.bankAcknowledgement.status) },
    { label: STEP_LABELS[9], state: stepFromStatus(r.reconciliation.status) },
    { label: STEP_LABELS[10], state: r.audit.auditRecordId ? 'success' : 'failed' },
  ];
}

const dot: Record<StepState, string> = {
  pending: 'bg-slate-600',
  running: 'bg-blue-500 animate-pulse',
  success: 'bg-green-500',
  failed: 'bg-red-500',
  unsupported: 'bg-slate-400',
  warning: 'bg-amber-500',
};

const text: Record<StepState, string> = {
  pending: 'text-slate-500',
  running: 'text-blue-300',
  success: 'text-green-300',
  failed: 'text-red-300',
  unsupported: 'text-slate-400',
  warning: 'text-amber-300',
};

export function ProcessingTimeline({ steps }: { steps: TimelineStep[] }): JSX.Element {
  return (
    <ol className="space-y-2" aria-label="Processing timeline">
      {steps.map((s) => (
        <li key={s.label} className="flex items-center gap-3">
          <span className={`inline-block h-3 w-3 rounded-full ${dot[s.state]}`} aria-hidden="true" />
          <span className={`text-sm ${text[s.state]}`}>{s.label}</span>
          <span className="ml-auto text-xs uppercase tracking-wide text-slate-500">{s.state}</span>
        </li>
      ))}
    </ol>
  );
}
